import type { ScheduleVoyage } from "../../shared/api/types";
import type { TimelineGroup, TimelineItem } from "../../shared/vis-timeline/Timeline";
import { darken, hatchVariant, textOn } from "./colors";

// Neutral grey used wherever a terminal has no (user or auto) colour
// yet. Exported — don't hard-code the hex at use sites.
export const FALLBACK_HEX = "#9ca3af";

// Selection-match predicates shared by the selection-sync effect and
// the compact tooltip dock. Exact match disambiguates pilot-in vs
// pilot-out at the same terminal; the broad key is the fallback when a
// patch-fetch delivered a different arrivalLocal than the selection
// captured.
export interface SelectionKey {
  vesselCode: string;
  voyageNo: string;
  portName: string;
  arrivalLocal: string;
}

export function matchesSelectionBroad(v: ScheduleVoyage, sel: SelectionKey): boolean {
  return v.vesselCode === sel.vesselCode
    && v.voyageNo === sel.voyageNo
    && v.portName === sel.portName;
}

export function matchesSelectionExact(v: ScheduleVoyage, sel: SelectionKey): boolean {
  return matchesSelectionBroad(v, sel) && v.arrivalLocal === sel.arrivalLocal;
}

// First candidate whose voyages match `sel`, preferring an exact
// (arrivalLocal) match over the broad fallback across the WHOLE list —
// i.e. an exact match later in the list beats a broad match earlier.
// `voyagesOf` maps a candidate to the voyages to test, so this serves
// both a plain voyage list (v => [v]) and a timeline-item list
// (item => item.voyages).
export function findMatchingBy<T>(
  candidates: readonly T[],
  sel: SelectionKey,
  voyagesOf: (c: T) => readonly ScheduleVoyage[],
): T | undefined {
  for (const c of candidates) {
    if (voyagesOf(c).some((v) => matchesSelectionExact(v, sel))) return c;
  }
  for (const c of candidates) {
    if (voyagesOf(c).some((v) => matchesSelectionBroad(v, sel))) return c;
  }
  return undefined;
}

export function findMatchingVoyage(
  voyages: readonly ScheduleVoyage[],
  sel: SelectionKey,
): ScheduleVoyage | undefined {
  return findMatchingBy(voyages, sel, (v) => [v]);
}

export function voyageItemId(v: ScheduleVoyage): string {
  return `${v.voyageReference}|${v.voyageNo}|${v.portName}|${v.arrivalLocal}`;
}

// Map an upstream segment label to its short code. Upstream currently
// emits "ALL AUTOS" / "ALL H&H" / "ALL NCC"; the latter hides Roll-
// Trailer and Breakbulk inside one bucket. Falls back to the raw label
// so future granular segments surface as-is until the mapping catches up.
export function segmentCode(segment: string): string {
  const s = segment.toUpperCase();
  if (s.includes("AUTO")) return "C";
  if (s.includes("H&H")) return "HH";
  if (s.includes("ROLL")) return "RT";
  if (s.includes("BREAK")) return "BB";
  if (s.includes("NCC")) return "NCC";
  return segment;
}

// Mine peer voyageReferences from a stop's voyageCargoHandlings array.
// Each externalRef is a `;`-delimited list of `<L|D>-<voyageRef>` tokens
// linking to the other half of a cargo movement. Refs matching the
// caller's own voyageReference are filtered out so a self-reference
// (`L-EA614` at an EA614 load stop) doesn't surface as a peer.
export function extractHandlingPeers(
  handlings: ReadonlyArray<{ externalRef: string }> | undefined,
  selfRef: string,
): string[] {
  if (!handlings || handlings.length === 0) return [];
  const peers = new Set<string>();
  for (const h of handlings) {
    if (!h.externalRef) continue;
    for (const part of h.externalRef.split(";")) {
      const m = part.trim().match(/^([LD])-(\S+)$/i);
      if (!m) continue;
      const peerRef = m[2]!;
      if (peerRef !== selfRef) peers.add(peerRef);
    }
  }
  return [...peers];
}

export function itemStyle(hex: string): string {
  return `background-color:${hex};border-color:${darken(hex, 0.25)};color:${textOn(hex)};`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case "&": return "&amp;";
      case "<": return "&lt;";
      case ">": return "&gt;";
      case '"': return "&quot;";
      default:  return "&#39;";
    }
  });
}

export function voyageContent(v: ScheduleVoyage, allRefs?: string[]): string {
  // Bar label shows every voyageReference for combined slots
  // ("AE607 + EA618") so the user sees both codes at a glance without
  // hovering for the tooltip. Solo bars get just the primary ref.
  const refs = allRefs && allRefs.length > 1 ? allRefs.join(" + ") : v.voyageReference;
  return `${escapeHtml(v.vesselName)} <span class="vref">${escapeHtml(refs)}</span>`;
}

export interface VoyageItem extends TimelineItem {
  // Primary voyage — what click handlers open by default.
  voyage: ScheduleVoyage;
  // Every voyage in the cluster (length 1 = solo, ≥2 = combined slot).
  voyages: ScheduleVoyage[];
  // Peer voyageReferences from overlapVoyageRefs that aren't already
  // represented by a cluster member — surfaced in the bar label so the
  // user sees every code that shares the call even when the schedule
  // API hides one of the co-calling refs (e.g. AH605's BEZEE entry
  // advertises ED610 as a peer, but the schedule's whole-port-stay
  // times for ED610 surface only the second visit a week later; the
  // first visit only shows up in voyage-details).
  peerRefs: string[];
}

export interface VoyageCluster {
  voyages: ScheduleVoyage[];
  // See VoyageItem.peerRefs.
  peerRefs: string[];
  start: string;
  end: string;
}

// Filter out refs upstream sometimes returns malformed — EA621 has
// been seen as ["621"] (digits-only truncation) and the empty string
// also surfaces occasionally. Real refs are 2-letter prefix + digits
// (length 5), so a "≥4 chars and contains at least one letter" rule
// catches the malformed cases without false-negating legitimate codes.
function isWellFormedRef(ref: string): boolean {
  if (typeof ref !== "string" || ref.length < 4) return false;
  return /[A-Za-z]/.test(ref);
}

// Group voyages of the same vessel calling at the same port into
// clusters of TIME-OVERLAPPING entries. Two stops at the same port
// that are separated in time (the vessel goes elsewhere and comes
// back) stay separate even if they share a voyageReference — they're
// physically distinct calls. A cluster of ≥2 voyages renders as a
// single "combined" bar spanning [min start, max end] with every
// voyageReference visible in the label and tooltip.
//
// overlapVoyageRefs is NOT used to trigger clustering — that's still
// time-overlap only, since transitive peer-ref clustering over-merges
// (commit 61624fc: ED610's two visits to a port would chain through a
// shared ref into one giant bar covering the gap between them).
//
// overlapVoyageRefs IS used for peer LABELS: the schedule API only
// reports one whole-port-stay window per voyage, so a vessel sharing
// a single call across two refs may have one ref absent from this
// window (e.g. AH605 says peer=[ED610] at BEZEE 06-05/06, but ED610's
// only schedule entry at BEZEE is the second visit on 06-13/14). The
// peer refs from each cluster member surface on the bar so the user
// sees every voyage code that shares the call even when the peer's
// standalone schedule entry sits at a different time (or is absent
// entirely). Malformed refs (digits-only, <4 chars) are dropped so
// upstream's ["621"] truncation doesn't surface as a phantom voyage.
export function clusterVoyagesByOverlap(voyages: ScheduleVoyage[]): VoyageCluster[] {
  // Bucket by (vesselCode, portName) — only voyages on the same ship at
  // the same port can ever share a slot.
  const buckets = new Map<string, ScheduleVoyage[]>();
  for (const v of voyages) {
    const key = `${v.vesselCode}|${v.portName}`;
    let arr = buckets.get(key);
    if (!arr) { arr = []; buckets.set(key, arr); }
    arr.push(v);
  }

  const clusters: VoyageCluster[] = [];
  for (const group of buckets.values()) {
    // Sweep entries sorted by arrival, opening a new cluster whenever
    // the next entry starts after the current cluster's running end.
    const sorted = [...group].sort((a, b) => a.arrivalLocal.localeCompare(b.arrivalLocal));
    let cur: { voyages: ScheduleVoyage[]; start: string; end: string } | null = null;
    const flush = () => {
      if (!cur) return;
      const clusterRefs = new Set(cur.voyages.map((v) => v.voyageReference));
      const peerRefs: string[] = [];
      const peerSeen = new Set<string>();
      for (const v of cur.voyages) {
        for (const r of v.overlapVoyageRefs ?? []) {
          if (clusterRefs.has(r)) continue;
          if (peerSeen.has(r)) continue;
          if (!isWellFormedRef(r)) continue;
          peerSeen.add(r);
          peerRefs.push(r);
        }
      }
      clusters.push({ voyages: cur.voyages, peerRefs, start: cur.start, end: cur.end });
      cur = null;
    };
    for (const v of sorted) {
      if (cur && v.arrivalLocal <= cur.end) {
        cur.voyages.push(v);
        if (v.departureLocal > cur.end) cur.end = v.departureLocal;
      } else {
        flush();
        cur = { voyages: [v], start: v.arrivalLocal, end: v.departureLocal };
      }
    }
    flush();
  }
  return clusters;
}

export function voyagesToTimelineItems(
  voyages: ScheduleVoyage[],
  portColors: Record<string, string>,
): VoyageItem[] {
  return clusterVoyagesByOverlap(voyages).map((c) => {
    // c.voyages is pre-sorted by arrival in clusterVoyagesByOverlap, so
    // primary is the chronologically first call in the cluster — which
    // is what the double-click handler opens (state.voyages[item.id]
    // resolves to this primary).
    const primary = c.voyages[0]!;
    const hex = portColors[primary.portName] ?? FALLBACK_HEX;
    // Chronological + deduped: voyages are pre-sorted by arrival; dedup
    // so a vessel that calls the same terminal twice under the same
    // voyageReference doesn't show "AE607 + AE607". Peer refs (refs
    // from overlapVoyageRefs that aren't already in the cluster) are
    // appended after the cluster's own refs.
    const allRefs: string[] = [];
    const seen = new Set<string>();
    for (const v of c.voyages) {
      if (!seen.has(v.voyageReference)) {
        seen.add(v.voyageReference);
        allRefs.push(v.voyageReference);
      }
    }
    for (const r of c.peerRefs) {
      if (!seen.has(r)) {
        seen.add(r);
        allRefs.push(r);
      }
    }
    const isCombined = allRefs.length > 1;
    const item: VoyageItem = {
      // Primary voyage's id — each cluster is anchored on its primary,
      // and a voyage entry can only belong to one cluster, so primary
      // ids are globally unique. Selection + connection-line lookups
      // (items[voyageItemId(v)]) keep working unchanged.
      id: voyageItemId(primary),
      group: primary.portName,
      start: c.start,
      end: c.end,
      content: voyageContent(primary, allRefs),
      // No `title` — tooltip rendering goes through `tooltip.template` in
      // CalendarTimeline so we can return a real DOM Element (xss-filter
      // would otherwise strip the colour styling from a string).
      style: itemStyle(hex),
      voyage: primary,
      voyages: c.voyages,
      peerRefs: c.peerRefs,
    };
    if (isCombined) {
      item.className = `voyageItemCombined voyageItemCombined--${hatchVariant(hex)}`;
    }
    return item;
  });
}

export function portsToTimelineGroups(
  ports: string[],
  refs: Map<string, string>,
  // Optional builder for the lane label DOM. When provided, takes priority
  // over the default string content so CalendarTimeline can inject a swatch
  // + click-to-recolour + drag-to-reorder label per row. Falls back to the
  // plain code/name string when no builder is given (tests, etc.).
  buildLabel?: (portName: string, index: number) => HTMLElement,
): TimelineGroup[] {
  return ports.map((p, i) => ({
    id: p,
    content: buildLabel ? buildLabel(p, i) : (refs.get(p) ?? p),
    // Drives vis-timeline's `groupOrder: "order"` sort — without this the
    // DataSet's insertion order locks the lanes in place across updates,
    // so a drag-reorder of selectedPorts has no visible effect until a
    // full re-init.
    order: i,
  }));
}
